import { SeoHead } from '@/components/SeoHead';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Navigation } from '@/components/Navigation';
import { useSignupUrl } from '@/hooks/useSignupUrl';
import { withAudienceEntityType } from '@/lib/auth-url';
import { Footer } from '@/components/Footer';
import { CookieConsent } from '@/components/CookieConsent';
import { BiuraHero } from '@/components/biura/BiuraHero';
import { BiuraFeatures } from '@/components/biura/BiuraFeatures';
import { BiuraPricing } from '@/components/biura/BiuraPricing';
import { BiuraCTA } from '@/components/biura/BiuraCTA';
import { useLandingNavigationPaths } from '@/hooks/useLandingNavigationPaths';
import { useLandingSectionTracking } from '@/hooks/useLandingSectionTracking';
import { UrgencyBanner } from '@/components/dowod/UrgencyBanner';
import { ApprovalFlowVisual } from '@/components/dowod/ApprovalFlowVisual';
import { ReadWithAI } from '@/components/ReadWithAI';
import { useTranslation } from '@/i18n/i18n';
import { RADIAN_URL } from '@/lib/radian';

const seoContent = {
  pl: {
    title: 'Bookeper dla biur rachunkowych — kontrola deklaracji i źródeł',
    description:
      'Workspace dla biur rachunkowych pracujących z klientami JDG: dokumenty, kalkulatory i deklaracje przygotowane do sprawdzenia oraz zatwierdzenia.',
    url: 'https://www.bookeper.pl/biura',
  },
  en: {
    title: 'Bookeper for Accounting Firms — Review-Ready Client Work',
    description:
      'Workspace for accounting firms serving Polish sole proprietors: documents, calculators, and declarations prepared for review and approval.',
    url: 'https://www.bookeper.pl/en/biura',
  },
  uk: {
    title: 'Bookeper для бухгалтерських фірм — робота з клієнтами під контролем',
    description:
      'Робочий простір для бухгалтерських фірм, які обслуговують ФОП у Польщі: документи, калькулятори та декларації для перевірки й затвердження.',
    url: 'https://www.bookeper.pl/uk/biura',
  },
} as const;

export default function Biura() {
  const { t, locale } = useTranslation();
  const signupUrl = useSignupUrl({ entityType: 'biuro', source: 'biura_nav' });
  const seo = seoContent[locale];
  const ksefRef = useLandingSectionTracking('biura_ksef');
  const { withSearch } = useLandingNavigationPaths();
  // Canonical PL KSeF path only — localized /en|/uk/ksef-automatycznie rewrite to
  // /index.html (homepage metadata), so mirror the /jdg precedent and stay crawl-safe.
  const localizedKsefPath = withAudienceEntityType(withSearch('/ksef-automatycznie'), 'biuro');

  return (
    <div className="min-h-screen pt-16 md:pt-20">
      <SeoHead>
        <html lang={locale} />
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:url" content={seo.url} />
        <meta property="og:type" content="website" />
        <link rel="alternate" hrefLang="pl" href="https://www.bookeper.pl/biura" />
        <link rel="alternate" hrefLang="en" href="https://www.bookeper.pl/en/biura" />
        <link rel="alternate" hrefLang="uk" href="https://www.bookeper.pl/uk/biura" />
        <link rel="alternate" hrefLang="x-default" href="https://www.bookeper.pl/biura" />
      </SeoHead>
      <Navigation
        cta={{ label: t('nav.biuraCta'), href: signupUrl, trackId: 'navigation_biura' }}
      />
      <main>
        <BiuraHero />
        <ReadWithAI page="biura" />
        <BiuraFeatures />
        {/* KSeF story + locale-aware cross-link (R8) — mirrors the /jdg + /wdrozenia pattern */}
        <section
          ref={ksefRef}
          className="px-6 md:px-8 py-14 border-b border-border bg-background"
          aria-labelledby="biura-ksef-heading"
        >
          <div className="max-w-6xl mx-auto">
            <h2
              id="biura-ksef-heading"
              className="text-3xl md:text-4xl font-semibold tracking-tight"
            >
              {t('biura.ksef.heading')}
            </h2>
            <p className="mt-3 text-muted-foreground max-w-2xl">{t('biura.ksef.description')}</p>
            <Link
              to={localizedKsefPath}
              className="mt-4 inline-flex items-center gap-1 text-sm underline underline-offset-4 hover:no-underline"
            >
              {t('biura.ksef.link')}
              <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
            {/* Proof-then-pressure (BOO-3240 KTD6): the approval-flow proof
                renders above the deadline pressure. */}
            <div className="mt-8 max-w-2xl">
              <ApprovalFlowVisual />
            </div>
            <div className="mt-8 max-w-2xl">
              <UrgencyBanner />
            </div>
          </div>
        </section>
        <BiuraPricing />
        <BiuraCTA />
        <aside className="px-6 md:px-8 py-8 border-t border-border bg-background-alt">
          <div className="max-w-6xl mx-auto flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground max-w-2xl">{t('biura.radian.lead')}</p>
            <a
              href={RADIAN_URL}
              rel="noopener"
              data-testid="biura-radian-link"
              className="inline-flex items-center gap-1 text-sm font-medium underline underline-offset-4 hover:no-underline"
            >
              {t('footer.radian')}
            </a>
          </div>
        </aside>
      </main>
      <Footer />
      <CookieConsent />
    </div>
  );
}
